import dayjs, { Dayjs } from "dayjs";
import { DateRange } from "../models/time";

export type BlockRange = { start: Dayjs; end: Dayjs };

export function parseBlockedRanges(
  listBlockDate: DateRange[],
  fmt = "YYYY-MM-DD HH:mm"
): BlockRange[] {
  const blockedRanges = listBlockDate
    .map((r) => ({
      start: dayjs(r.FromDate),
      end: dayjs(r.ToDate).add(15, "minute"),
    }))
    .map((r) => (r.start.isAfter(r.end) ? { start: r.end, end: r.start } : r));
  return blockedRanges;
}

export function overlapsDay(d: Dayjs, r: BlockRange): boolean {
  const dayStart = d.startOf("day");
  const dayEnd = d.endOf("day");
  return !(r.end.isBefore(dayStart) || r.start.isAfter(dayEnd));
}

export function intervalsForDay(
  d: Dayjs,
  ranges: BlockRange[]
): Array<[number, number]> {
  const dayStart = d.startOf("day");
  const ints: Array<[number, number]> = [];

  for (const r of ranges) {
    const start = r.start;
    const end = r.end;

    const dayEnd = d.endOf("day");

    if (start.isAfter(dayEnd) || end.isBefore(dayStart)) continue;

    const s = Math.max(0, start.diff(dayStart, "minute"));
    const e = Math.min(24 * 60 - 1, end.diff(dayStart, "minute"));

    if (e >= s) ints.push([s, e]);
  }

  ints.sort((a, b) => a[0] - b[0]);

  const merged: Array<[number, number]> = [];
  for (const it of ints) {
    if (!merged.length || it[0] > merged[merged.length - 1][1] + 1) {
      merged.push([...it]);
    } else {
      merged[merged.length - 1][1] = Math.max(
        merged[merged.length - 1][1],
        it[1]
      );
    }
  }

  return merged;
}

export function isDateDisabled(
  current: Dayjs,
  blocked: BlockRange[],
  startOfToday: Dayjs,
  endOfNextMonth: Dayjs
): boolean {
  const outsideWindow =
    current.isBefore(startOfToday, "day") ||
    current.isAfter(endOfNextMonth, "day");
  const hitsBlockedDay = blocked.some((r) => overlapsDay(current, r));
  return outsideWindow || hitsBlockedDay;
}

export function buildDisabledTime(
  date: Dayjs,
  blocked: BlockRange[]
): {
  disabledHours: () => number[];
  disabledMinutes: (hour: number) => number[];
} {
  const merged = intervalsForDay(date, blocked);

  const disabledByHour: Record<number, number[]> = {};
  for (let h = 0; h < 24; h++) disabledByHour[h] = [];

  for (const [mStart, mEnd] of merged) {
    for (let cur = mStart; cur <= mEnd; cur++) {
      const hour = Math.floor(cur / 60);
      const minute = cur % 60;
      disabledByHour[hour].push(minute);
    }
  }
  for (let h = 0; h < 24; h++) {
    disabledByHour[h] = Array.from(new Set(disabledByHour[h])).sort(
      (a, b) => a - b
    );
  }

  const disabledHours = Object.entries(disabledByHour)
    .filter(([, mins]) => mins.length === 60)
    .map(([h]) => Number(h));

  return {
    disabledHours: () => disabledHours,
    disabledMinutes: (hour: number) => disabledByHour[hour] ?? [],
  };
}

export function applyPastTimeToday(
  date: Dayjs,
  time: ReturnType<typeof buildDisabledTime>
) {
  const now = dayjs();

  const cutoff = now.add(1, "hour");
  if (!date.isSame(now, "day")) return time;

  const disabledMinutes = (hour: number) => {
    const base = time.disabledMinutes(hour);
    if (hour < cutoff.hour()) {
      return Array.from({ length: 60 }, (_, i) => i);
    }

    if (hour === cutoff.hour()) {
      return Array.from(
        new Set([
          ...base,
          ...Array.from({ length: cutoff.minute() + 1 }, (_, i) => i),
        ])
      ).sort((a, b) => a - b);
    }

    return base;
  };

  const disabledHours = () => {
    const result = new Set<number>();

    for (let h = 0; h < cutoff.hour(); h++) {
      result.add(h);
    }

    if (disabledMinutes(cutoff.hour()).length === 60) {
      result.add(cutoff.hour());
    }

    for (const h of time.disabledHours()) {
      result.add(h);
    }

    return Array.from(result).sort((a, b) => a - b);
  };
  return { disabledHours, disabledMinutes };
}

export {};
